import type { ComponentDiagnostic } from "@swot/base-components";
import { createDiagnostic, throwCmtsCanonicalError } from "../api/cmtsApiErrors.js";

export function asRecord(value: unknown): Record<string, unknown> { return typeof value === "object" && value !== null ? value as Record<string, unknown> : {}; }
export function readString(source: unknown, keys: readonly string[], fallback = "Not available"): string {
  const record = asRecord(source);
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return fallback;
}
export function readBoolean(source: unknown, keys: readonly string[], fallback = false): boolean {
  const record = asRecord(source);
  for (const key of keys) {
    if (typeof record[key] === "boolean") return record[key] as boolean;
    if (typeof record[key] === "string") return ["true", "yes", "1"].includes(String(record[key]).toLowerCase());
  }
  return fallback;
}
export function readNumber(source: unknown, keys: readonly string[], fallback = 0): number {
  const record = asRecord(source);
  for (const key of keys) {
    const value = record[key];
    const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}
export function readDateIso(source: unknown, keys: readonly string[], fallback = ""): string {
  const value = readString(source, keys, fallback);
  if (!value || value === fallback) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? fallback : date.toISOString();
}
export function readArray<T>(source: unknown, keys: readonly string[], mapper: (value: unknown) => T): readonly T[] {
  const record = asRecord(source);
  for (const key of keys) if (Array.isArray(record[key])) return (record[key] as unknown[]).map(mapper);
  return [];
}
export function enumOrUnknown<T extends string>(value: string, allowed: readonly T[], unknownValue: T): T { return (allowed as readonly string[]).includes(value) ? value as T : unknownValue; }
export function mapValidationErrors(source: unknown): readonly ComponentDiagnostic[] {
  const record = asRecord(source);
  const errors = Array.isArray(record.errors) ? record.errors : [];
  return errors.map((e) => createDiagnostic(readString(e, ["code"], "CMTS_VALIDATION"), readString(e, ["message"], "Validation failed"), "error", readString(e, ["field"], ""))).filter((d) => Boolean(d.message));
}
export function hasRestrictedMarker(source: unknown): boolean { return readBoolean(source, ["restricted", "visibilityRestricted", "restrictedEvidenceExcluded", "internalNotesExcluded"], false); }

export function requireRecordForMapping(value: unknown, mapperName: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    throwCmtsCanonicalError({ code: "CMTS_ERR_MAPPER_FAILURE", message: `${mapperName} expected a record object.`, details: { mapperName, receivedType: typeof value } });
  }
  return value as Record<string, unknown>;
}
export function readNumberOrThrow(source: unknown, keys: readonly string[], mapperName: string): number {
  const record = asRecord(source);
  for (const key of keys) {
    const value = record[key];
    const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
    if (Number.isFinite(n)) return n;
  }
  throwCmtsCanonicalError({ code: "CMTS_ERR_UNKNOWN_DATE_AMOUNT_VALUE", message: `${mapperName} could not resolve a finite numeric value from ${keys.join(", ")}.`, field: keys[0], details: { mapperName, keys, source: record } });
}
export function readDateIsoOrThrow(source: unknown, keys: readonly string[], mapperName: string): string {
  const record = asRecord(source);
  for (const key of keys) {
    const value = record[key];
    if (value === undefined || value === null || String(value).trim() === "") continue;
    const date = new Date(String(value));
    if (!Number.isNaN(date.valueOf())) return date.toISOString();
  }
  throwCmtsCanonicalError({ code: "CMTS_ERR_UNKNOWN_DATE_AMOUNT_VALUE", message: `${mapperName} could not resolve a valid ISO date/time from ${keys.join(", ")}.`, field: keys[0], details: { mapperName, keys, source: record } });
}
export function enumOrThrow<T extends string>(value: string, allowed: readonly T[], mapperName: string, field = "status"): T {
  if ((allowed as readonly string[]).includes(value)) return value as T;
  throwCmtsCanonicalError({ code: "CMTS_ERR_UNKNOWN_ENUM_STATUS", message: `${mapperName} received unsupported ${field} value ${value || "<empty>"}.`, field, details: { mapperName, value, allowed } });
}
